import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Star, ExternalLink } from "lucide-react";

/**
 * Lazy on-demand fetcher for Google Places aggregate rating + review count.
 *
 * - Calls `GET /api/campsites/{id}/places-summary` on mount.
 * - Backend handles 30-day MongoDB caching, so most requests are <50ms.
 * - Renders NOTHING when the feature is disabled (no API key), the campsite
 *   isn't on Google, or the fetch silently fails — never breaks the page.
 *
 * Visual: small Google-themed pill that sits beside CampCompass's own
 * rating star, e.g. `Google 4.6 · 392 reviews ↗` (clickable to Google Maps).
 */
export default function GooglePlacesPill({ campsiteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    if (!campsiteId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const res = await api(`/campsites/${encodeURIComponent(campsiteId)}/places-summary`);
        if (!cancel) setData(res);
      } catch {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [campsiteId]);

  // Silently hide while loading the very first time — avoids layout shift.
  if (loading || !data) return null;
  // Feature disabled (no API key on server), or Google has no listing.
  if (data.enabled === false) return null;
  if (data.zero_results) return null;
  if (typeof data.rating !== "number" || data.rating <= 0) return null;

  const reviews = data.user_ratings_total || 0;
  const href = data.google_maps_url
    || (data.place_id
      ? `https://www.google.com/maps/place/?q=place_id:${data.place_id}`
      : null);

  const inner = (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-[#dadce0] text-[11px] mono text-[#3c4043] hover:bg-[#f8f9fa] transition-colors">
      <span className="font-semibold tracking-tight">
        <span className="text-[#4285F4]">G</span>
        <span className="text-[#EA4335]">o</span>
        <span className="text-[#FBBC04]">o</span>
        <span className="text-[#4285F4]">g</span>
        <span className="text-[#34A853]">l</span>
        <span className="text-[#EA4335]">e</span>
      </span>
      <Star className="w-3 h-3 fill-[#fbbc04] text-[#fbbc04]" />
      <span className="font-bold text-[#1f1f1f]">{data.rating.toFixed(1)}</span>
      {reviews > 0 && (
        <span className="text-[#5f6368]">· {reviews.toLocaleString("en-GB")} {reviews === 1 ? "review" : "reviews"}</span>
      )}
      {href && <ExternalLink className="w-3 h-3 text-[#5f6368]" />}
    </span>
  );

  if (!href) {
    return (
      <div data-testid="google-places-pill" className="mt-2">
        {inner}
      </div>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="google-places-pill"
      className="inline-block mt-2"
      title="View on Google Maps"
    >
      {inner}
    </a>
  );
}
